/**
 * The realtime contract. Mirrors the backend's
 * infrastructure/Services/Socket/SocketEvents.ts; change both together.
 *
 * Events are invalidation hints, not data: they say what changed and who
 * changed it, and the cache refetches through the normal REST reads.
 */

import type { WorkshopQuickCreateRef } from "./workshop.type";

/** The single wire event every realtime message arrives on. */
export const REALTIME_EVENT = "realtime:event";

/** Handshake rejection message; the client refreshes its token on it. */
export const SOCKET_UNAUTHORIZED = "unauthorized";

export type RealtimeFactRef = { id: string; name: string };

export interface RealtimePayloads {
  /** Facts written outside the proposal flow (accepts arrive as proposals "decided"). */
  "universe.facts.added": { facts: RealtimeFactRef[]; source: "signal" };
  "universe.facts.removed": { facts: RealtimeFactRef[] };
  /** A fact was filed under (or taken off) a competitor. */
  "universe.facts.updated": { facts: RealtimeFactRef[] };
  "universe.entities.changed": {
    action: "created" | "updated" | "deleted" | "merged";
    entityId: string;
    name: string;
    /** merged only: the entity the source was folded into. */
    targetEntityId?: string;
  };
  /** Bulk rewrite (admin retag): refetch everything universe-shaped. */
  "universe.graph.rebuilt": { reason: "retag" };
  "universe.proposals.changed": {
    action: "proposed" | "edited" | "withdrawn" | "decided";
    sessionId: string;
    proposalIds: string[];
    /** decided only: the facts this batch's accepts wrote. */
    acceptedFacts?: RealtimeFactRef[];
  };
  "brand.updated": { fields: Array<"name" | "logo"> };
  /** Sent to the project room and to each added or removed person. */
  "workshop.members.changed": { projectId: string; added: string[]; removed: string[] };
  /** Sent to everyone in the thread: thread lists are per person. */
  "workshop.threads.changed": {
    projectId: string;
    sessionId: string;
    /** "activity": new messages moved it up the list. */
    action: "shared" | "participants" | "renamed" | "deleted" | "activity";
  };
  "workshop.project.updated": {
    projectId: string;
    fields: Array<"name" | "description" | "status">;
  };
  /**
   * A thread moved to another project (its OP moved it). Sent to both projects'
   * rooms and to the OP's `user:` room: both thread lists and project cards change.
   */
  "workshop.thread.moved": {
    sessionId: string;
    fromProjectId: string;
    toProjectId: string;
    actorUserId: string;
  };
  "workshop.project.deleted": { projectId: string };
  "workshop.artifacts.changed": WorkshopItemsChangedPayload;
  /** Artifact notes (ids are note ids); an artifact's open-note count moves with them. */
  "workshop.notes.changed": WorkshopItemsChangedPayload;
  "workshop.ideas.changed": WorkshopItemsChangedPayload;
  "workshop.context.changed": WorkshopItemsChangedPayload;
  "workshop.media.changed": WorkshopItemsChangedPayload;
  "workshop.files.changed": WorkshopItemsChangedPayload;
  "workshop.deployments.changed": WorkshopItemsChangedPayload;
  /**
   * A notification was written for you (backend #132): refetch the bell when
   * `organizationId` is the selected organization.
   */
  "notification.created": {
    notificationId: string;
    organizationId: string;
    kind: string;
    slotId: string | null;
  };
  /**
   * Content calendar hints (ids only: guests share the brand room). A month's
   * own state moved: created, planning, planned, failed, re-planned, re-paced,
   * archived or otherwise updated.
   */
  "calendar.plan.changed": {
    planId: string;
    year: number;
    month: number;
    action: CalendarPlanAction;
  };
  /**
   * Posts in one month changed; `parts` says which of their reads to refresh.
   * `slotIds` is empty for a month-wide write that names no posts (an email to
   * the client, a whole send pulled back): refresh the whole month.
   */
  "calendar.slots.changed": {
    planId: string;
    slotIds: string[];
    action: "created" | "updated" | "removed";
    parts: CalendarPart[];
  };
  /** The brand's calendar settings (cadence, schedule, strategy scope, pillars). */
  "calendar.settings.changed": { fields: Array<"cadence" | "schedule" | "scope" | "pillars"> };
  /** Pasted-link media finished saving (or failed). */
  "calendar.media.changed": { mediaIds: string[] };
  /**
   * Market signals were written: by a member, the chat agent (SetMarketSignal)
   * or the daily expiry sweep. Refetch the list and the pending badge.
   */
  "signals.changed": { signalIds: string[]; action: SignalsAction };
  /** A field reporter run filed its report, with the pending signals it staged. */
  "signals.report.filed": { reportId: string; runId: string | null; signalIds: string[] };
  /**
   * A strategy was created, edited, moved status or deleted. Refetch the list
   * and, when it is open, the strategy. `chapters` names the document's
   * chapters an "updated" write touched.
   */
  "strategy.changed": {
    strategyId: string;
    action: "created" | "updated" | "status" | "deleted";
    parts: Array<"row" | "document" | "media">;
    chapters: string[];
  };
}

/** What happened to the market signals a `signals.changed` hint names. */
export type SignalsAction =
  | "created"
  | "accepted"
  | "updated"
  | "superseded"
  | "dismissed"
  | "graduated"
  | "expired";

/**
 * Which of a calendar post's reads a change touched. The month itself is
 * refreshed for every part but `comments` and `live`, which it does not carry.
 */
export type CalendarPart =
  | "post"
  | "ideas"
  | "concept"
  | "brief"
  | "review"
  | "comments"
  | "files"
  | "publishing"
  | "live"
  | "risk";

export type CalendarPlanAction =
  | "created"
  | "planning"
  | "planned"
  | "failed"
  /** Stop planning: the run's result was thrown away and the month is not planned. */
  | "cancelled"
  | "replanned"
  | "cadence"
  | "archived"
  | "updated";

/** A write to one kind of workshop project item. */
export type WorkshopItemsChangedPayload = {
  projectId: string;
  ids: string[];
  action: "created" | "updated" | "removed";
};

export type RealtimeEventType = keyof RealtimePayloads;

export type RealtimeActor =
  | { kind: "user"; userId: string; name: string | null }
  /** The brand agent, working in a chat turn a member started. */
  | { kind: "agent"; onBehalfOf: { userId: string; name: string | null } | null }
  | { kind: "system" };

export type RealtimeEnvelope<T extends RealtimeEventType = RealtimeEventType> = {
  [K in T]: {
    id: string;
    type: K;
    brandId: string;
    actor: RealtimeActor;
    at: string;
    data: RealtimePayloads[K];
  };
}[T];

export type BrandSubscribeResult =
  | { ok: true }
  | { ok: false; error: "invalid_brand" | "forbidden" | "error" };

// ---- Workshop live signals --------------------------------------------------
//
// The one exception to "events are hints": a shared workshop thread streams to
// a room of people checked when they joined it. Frames carry small content
// (message text, stream deltas, typing, presence) and are never cached as
// data; a rejoin refetches the thread.

/** The wire event every workshop live signal arrives on. */
export const WORKSHOP_LIVE_EVENT = "workshop:live";

export type WorkshopLivePerson = { userId: string; name: string | null };

/**
 * What the AI in a shared thread is visibly doing. Its thinking is never shown:
 * "working" only comes once it has committed to a reply (a tool call or text),
 * for the pauses between its steps.
 */
export type WorkshopAgentActivity = "idle" | "working" | "tool" | "writing";

export type WorkshopPresenceFrame = {
  type: "presence";
  sessionId: string;
  projectId: string;
  users: Array<WorkshopLivePerson & { state: "active" | "away" }>;
  replying: boolean;
};

export type WorkshopLiveFrame =
  | {
      type: "message.created";
      sessionId: string;
      message: {
        id: string;
        authorUserId: string;
        authorName: string | null;
        content: string;
        createdAt: string;
        /** Set when it answers a card: the assistant message holding it. */
        respondsTo?: string | null;
        /** The Quick Create it was sent with (its pill). */
        quickCreates?: WorkshopQuickCreateRef[];
      };
    }
  /** The assistant read these messages: the newest one it has seen carries its mark. */
  | { type: "messages.seen"; sessionId: string; messageIds: string[]; seenAt: string }
  /**
   * A new assistant row started; `actor` is who it acts for. `createdAt` is the
   * row's: the live reply sits there in the transcript, above anything sent later.
   */
  | {
      type: "segment.started";
      sessionId: string;
      messageId: string;
      createdAt: string;
      actor: WorkshopLivePerson;
    }
  /**
   * One stream frame of that row, in the SSE shapes (chunk, tool_start,
   * tool_end, usage, complete, aborted, error; never thinking). `offset` is
   * the text length before a chunk.
   */
  | {
      type: "stream";
      sessionId: string;
      messageId: string;
      frame: Record<string, unknown>;
      offset?: number;
    }
  | {
      type: "segment.ended";
      sessionId: string;
      messageId: string;
      status: "complete" | "error" | "aborted";
      silent: boolean;
    }
  | {
      type: "run.notice";
      sessionId: string;
      messageId: string;
      notice: "budget" | "access" | "error";
    }
  | { type: "agent.state"; sessionId: string; state: WorkshopAgentActivity; toolName?: string }
  | { type: "typing"; sessionId: string; userId: string; name: string | null; typing: boolean }
  | WorkshopPresenceFrame;

export type WorkshopSubscribeResult =
  | { ok: true }
  | { ok: false; error: "invalid" | "forbidden" | "error" };

export type WorkshopThreadJoinResult =
  | { ok: true; presence: WorkshopPresenceFrame; agentState: WorkshopAgentActivity }
  | { ok: false; error: "invalid" | "forbidden" | "error" };

export interface RealtimeServerEvents {
  "realtime:event": (event: RealtimeEnvelope) => void;
  "workshop:live": (frame: WorkshopLiveFrame) => void;
}

export interface RealtimeClientEvents {
  "brand:subscribe": (brandId: string, ack: (result: BrandSubscribeResult) => void) => void;
  "brand:unsubscribe": (brandId: string) => void;
  "workshop:project:subscribe": (
    projectId: string,
    ack: (result: WorkshopSubscribeResult) => void,
  ) => void;
  "workshop:project:unsubscribe": (projectId: string) => void;
  "workshop:thread:join": (
    payload: { sessionId: string; state?: "active" | "away" },
    ack: (result: WorkshopThreadJoinResult) => void,
  ) => void;
  "workshop:thread:leave": (sessionId: string) => void;
  "workshop:thread:typing": (payload: { sessionId: string; typing: boolean }) => void;
  "workshop:thread:presence": (payload: {
    sessionId: string;
    state: "active" | "away";
  }) => void;
}
